import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Clock, Calendar, Play, Sparkles, ArrowRight, Shuffle, Gift, Palette } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePlayer } from '@/contexts/PlayerContext';

const MOODS = [
    { v: 'calm', label: 'Calm', emoji: '🌿' },
    { v: 'anxious', label: 'Anxious', emoji: '🌧' },
    { v: 'tired', label: 'Tired', emoji: '🌙' },
    { v: 'energized', label: 'Energized', emoji: '☀️' },
    { v: 'sad', label: 'Sad', emoji: '🫧' },
    { v: 'grateful', label: 'Grateful', emoji: '🍃' },
    { v: 'stressed', label: 'Stressed', emoji: '🌫' },
    { v: 'neutral', label: 'Neutral', emoji: '◯' },
];

function timeGreeting(name) {
    const h = new Date().getHours();
    const first = (name || '').split(' ')[0] || 'friend';
    if (h < 5) return `Still up, ${first}?`;
    if (h < 12) return `Good morning, ${first}`;
    if (h < 17) return `Good afternoon, ${first}`;
    if (h < 21) return `Good evening, ${first}`;
    return `Good night, ${first}`;
}

function timeBucket() {
    const h = new Date().getHours();
    if (h >= 5 && h < 11) return 'morning';
    if (h >= 11 && h < 17) return 'midday';
    if (h >= 17 && h < 21) return 'evening';
    return 'night';
}

const BUCKET_FOCUS = {
    morning: ['focus', 'self-love', 'gratitude'],
    midday: ['focus', 'stress'],
    evening: ['stress', 'sleep', 'self-love'],
    night: ['sleep'],
};
const BUCKET_LABEL = {
    morning: 'A bright way in',
    midday: 'A reset for the middle of the day',
    evening: 'Unwinding the day',
    night: 'A descent into rest',
};

function chooseSuggestion(meditations, focus, sessionLen, bucket, seed) {
    if (!meditations.length) return null;
    const bucketFocus = BUCKET_FOCUS[bucket] || [];
    // Score each: +3 user focus match, +2 bucket match, +1 close duration
    const scored = meditations.map((m) => {
        let s = 0;
        if (focus.some((f) => m.focus_areas.includes(f))) s += 3;
        if (bucketFocus.some((f) => m.focus_areas.includes(f))) s += 2;
        if (sessionLen && Math.abs(m.duration_minutes - sessionLen) <= 5) s += 1;
        return { m, s };
    });
    const maxScore = Math.max(...scored.map((x) => x.s));
    const top = scored.filter((x) => x.s === maxScore);
    const pool = top.length ? top : scored;
    return pool[seed % pool.length].m;
}

function pickedReason(m, bucket, focus) {
    const bucketFocus = BUCKET_FOCUS[bucket] || [];
    const matched = m.focus_areas.find((f) => focus.includes(f));
    if (matched) return `Because you wanted to bring more ${matched} into your days.`;
    const bucketMatch = m.focus_areas.find((f) => bucketFocus.includes(f));
    if (bucketMatch) return `${BUCKET_LABEL[bucket]} — chosen for ${bucketMatch}.`;
    return BUCKET_LABEL[bucket] + '.';
}

export default function Home() {
    const { user } = useAuth();
    const { playTrack, setFullscreen } = usePlayer();
    const [stats, setStats] = useState(null);
    const [meditations, setMeditations] = useState([]);
    const [moodSaved, setMoodSaved] = useState(null);
    const [reco, setReco] = useState(null);
    const [recoLoading, setRecoLoading] = useState(false);
    const [shuffleSeed, setShuffleSeed] = useState(0);

    useEffect(() => {
        api.get('/sessions/stats').then((r) => setStats(r.data)).catch(() => {});
        api.get('/meditations').then((r) => setMeditations(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setRecoLoading(true);
        api.get('/recommendations/today')
            .then((r) => setReco(r.data))
            .catch(() => setReco(null))
            .finally(() => setRecoLoading(false));
    }, []);

    const bucket = timeBucket();

    // Choose suggestion: prefer recommendation picks, fall back to local heuristic.
    const picks = useMemo(() => {
        if (reco?.picks?.length) return reco.picks;
        if (!meditations.length) return [];
        const focus = user?.onboarding?.focus_areas || [];
        const sessionLen = parseInt(user?.onboarding?.session_length || '5', 10);
        const today = new Date().toISOString().slice(0, 10);
        const key = `qm_today_${today}_${bucket}`;
        if (shuffleSeed === 0) {
            const cached = localStorage.getItem(key);
            if (cached) {
                const found = meditations.find((m) => m.id === cached);
                if (found) return [found];
            }
        }
        const chosen = chooseSuggestion(
            meditations, focus, sessionLen, bucket,
            shuffleSeed || Math.floor(Math.random() * 1000),
        );
        if (chosen) localStorage.setItem(key, chosen.id);
        return chosen ? [chosen] : [];
    }, [reco, meditations, user, bucket, shuffleSeed]);

    const suggestion = picks[0] || null;
    const altPicks = picks.slice(1, 3);

    const reason = useMemo(() => {
        if (reco?.copy) return reco.copy;
        if (!suggestion) return '';
        return pickedReason(suggestion, bucket, user?.onboarding?.focus_areas || []);
    }, [reco, suggestion, bucket, user]);

    const shuffle = () => {
        const today = new Date().toISOString().slice(0, 10);
        const key = `qm_today_${today}_${bucket}`;
        localStorage.removeItem(key);
        // Force a fresh server recommendation by invalidating + refetching.
        setRecoLoading(true);
        api.get('/recommendations/today', { params: { _: Date.now() } })
            .then((r) => setReco(r.data))
            .catch(() => {})
            .finally(() => setRecoLoading(false));
        setShuffleSeed(Date.now());
    };

    const logMood = async (mood) => {
        try {
            await api.post('/mood', { mood, context: 'standalone' });
            setMoodSaved(mood);
            setTimeout(() => setMoodSaved(null), 2200);
        } catch {}
    };

    const startSuggestion = () => {
        if (!suggestion) return;
        playTrack({
            id: suggestion.id,
            title: suggestion.title,
            subtitle: suggestion.instructor,
            image: suggestion.cover_image,
            audio_url: suggestion.audio_url,
            kind: 'meditation',
            duration_minutes: suggestion.duration_minutes,
        });
        setFullscreen(true);
    };

    const playSpecific = (m) => {
        playTrack({
            id: m.id,
            title: m.title,
            subtitle: m.instructor,
            image: m.cover_image,
            audio_url: m.audio_url,
            kind: 'meditation',
            duration_minutes: m.duration_minutes,
        });
        setFullscreen(true);
    };

    return (
        <div className="space-y-8" data-testid="home-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Welcome back</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2" data-testid="home-greeting">{timeGreeting(user?.name)}</h1>
                    <p className="text-forest-soft mt-2 max-w-xl">
                        A small pocket of stillness, picked for the way today feels.
                    </p>
                </div>
                {stats && (
                    <div className="flex items-center gap-3" data-testid="home-quick-stats">
                        <Stat icon={<Flame size={16} />} label="Streak" value={`${stats.current_streak}d`} />
                        <Stat icon={<Clock size={16} />} label="Total" value={`${Math.round(stats.total_minutes)}m`} />
                        <Stat icon={<Calendar size={16} />} label="Sessions" value={`${stats.total_sessions}`} />
                    </div>
                )}
            </header>

            {/* Today's Suggestion */}
            <section className="qm-card overflow-hidden" data-testid="todays-suggestion">
                <div className="grid md:grid-cols-2">
                    <div className="p-7 md:p-9 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-[0.25em] text-forest-soft">Today’s suggestion</span>
                            <button
                                onClick={shuffle}
                                aria-label="Shuffle today's pick"
                                data-testid="shuffle-suggestion"
                                className="qm-pill text-xs border-forest/20 hover:bg-sage/30 inline-flex items-center gap-1"
                            >
                                <Shuffle size={12} /> Shuffle
                            </button>
                        </div>
                        <h2 className="font-serif text-3xl text-forest" data-testid="suggestion-title">{suggestion?.title || 'Loading a session…'}</h2>
                        <p className="text-forest-soft">{suggestion?.description}</p>
                        {reason && (
                            <p className="text-xs italic text-forest-soft" data-testid="suggestion-reason">{reason}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            {suggestion && (
                                <>
                                    <span className="qm-pill bg-sage/30 border-transparent">{suggestion.duration_minutes} min</span>
                                    <span className="qm-pill bg-dusty/40 border-transparent">{suggestion.category}</span>
                                </>
                            )}
                        </div>
                        <button
                            onClick={startSuggestion}
                            disabled={!suggestion}
                            data-testid="play-suggestion-button"
                            className="qm-btn-primary inline-flex items-center gap-2 mt-2"
                        >
                            <Play size={16} /> Begin
                        </button>
                    </div>
                    <div
                        className="hidden md:block min-h-[280px]"
                        style={{
                            backgroundImage: suggestion?.cover_image
                                ? `url(${suggestion.cover_image})`
                                : 'linear-gradient(160deg, #C9DDCD, #C8B6DB)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                </div>
            </section>

            {altPicks.length > 0 && (
                <section className="space-y-3" data-testid="also-for-you">
                    <div className="flex items-end justify-between">
                        <h3 className="font-serif text-2xl text-forest">Also for you</h3>
                        <span className="text-xs text-forest-soft uppercase tracking-[0.2em]">{reco?.bucket || bucket} · {reco?.dominant_mood || 'today'}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {altPicks.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => playSpecific(m)}
                                data-testid={`alt-pick-${m.id}`}
                                className="qm-card p-4 text-left flex items-center gap-3 hover:shadow-md transition group"
                            >
                                <div
                                    className="w-14 h-14 rounded-2xl shrink-0 bg-sage/40"
                                    style={m.cover_image ? { backgroundImage: `url(${m.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="font-serif text-lg text-forest truncate">{m.title}</div>
                                    <div className="text-xs text-forest-soft mt-0.5">{m.duration_minutes} min · {m.category}</div>
                                </div>
                                <Play size={16} className="text-forest opacity-50 group-hover:opacity-100" />
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Mood */}
            <section className="qm-card p-7 md:p-9" data-testid="mood-checkin">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h3 className="font-serif text-2xl text-forest">How are you, really?</h3>
                        <p className="text-forest-soft text-sm">A quiet check-in. Just for you.</p>
                    </div>
                    {moodSaved && <span className="text-sm text-forest animate-bloom" data-testid="mood-saved">Noted — thank you. 🌿</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                    {MOODS.map((m) => (
                        <button
                            key={m.v}
                            onClick={() => logMood(m.v)}
                            data-testid={`mood-${m.v}`}
                            className="qm-pill border-forest/20 hover:bg-sage/30"
                        >
                            <span className="mr-1">{m.emoji}</span>{m.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Quick links */}
            <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-quick-links">
                <QuickLink to="/app/library" title="Browse the library" subtitle="Curated meditations for every mood" />
                <QuickLink to="/app/create" title="Make your own" subtitle="AI meditations & visualizations" icon={<Sparkles size={18} />} />
                <QuickLink to="/app/sound" title="Colors of sound" subtitle="Focus · Study · Sleep · Relax" icon={<Palette size={18} />} />
                <QuickLink to="/app/gifts" title="Give a gift" subtitle="Premium for someone you love" icon={<Gift size={18} />} />
            </section>
        </div>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div className="qm-card px-4 py-2.5 flex items-center gap-2">
            <span className="text-forest-soft">{icon}</span>
            <span className="font-serif text-xl text-forest tabular-nums">{value}</span>
            <span className="text-xs text-forest-soft uppercase tracking-wider">{label}</span>
        </div>
    );
}

function QuickLink({ to, title, subtitle, icon }) {
    return (
        <Link to={to} data-testid={`quick-${title.replace(/\s+/g, '-').toLowerCase()}`} className="qm-card p-6 group hover:shadow-md transition">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="font-serif text-xl text-forest flex items-center gap-2">{icon}{title}</div>
                    <div className="text-sm text-forest-soft mt-1">{subtitle}</div>
                </div>
                <ArrowRight className="text-forest opacity-50 group-hover:translate-x-1 group-hover:opacity-100 transition" size={18} />
            </div>
        </Link>
    );
}
